import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  User, 
  Lock, 
  Save, 
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Mail,
  Building2,
  Clock,
  Globe
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';

function StaffSettingsPage() {
  const { t, isRTL } = useLanguage();
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [staffInfo, setStaffInfo] = useState(null);
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/staff/settings/profile`, {
        withCredentials: true
      });
      setProfile({
        name: response.data.user.name || '',
        email: response.data.user.email || ''
      });
      setStaffInfo(response.data.staff);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      showMessage('error', 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/staff/settings/profile`,
        { name: profile.name },
        { withCredentials: true }
      );
      showMessage('success', 'Profile updated successfully!');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    
    if (passwords.new_password.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/staff/settings/password`,
        {
          current_password: passwords.current_password,
          new_password: passwords.new_password
        },
        { withCredentials: true }
      );
      showMessage('success', 'Password changed successfully!');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 p-6" data-testid="staff-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t("settings.title")}</h1>
        <p className="text-gray-500 mt-1">Manage your account settings</p>
      </div>
      
      {/* Message Alert */}
      {message.text && (
        <div className={`
          flex items-center gap-3 p-4 rounded-lg
          ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : ''}
          ${message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : ''}
        `}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600" />
          )}
          <span className="font-medium">{message.text}</span>
        </div>
      )}
      
      {/* Settings Tabs */}
      <Tabs defaultValue="profile" className="w-full max-w-3xl">
        <TabsList className="grid grid-cols-3 w-full max-w-md bg-gray-100 p-1 rounded-xl">
          <TabsTrigger 
            value="profile" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="staff-profile-tab"
          >
            <User className="w-4 h-4 mr-2" />
            {t('settings.personal_info')}
          </TabsTrigger>
          <TabsTrigger 
            value="security" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="staff-security-tab"
          >
            <Lock className="w-4 h-4 mr-2" />
            {t('settings.security')}
          </TabsTrigger>
          <TabsTrigger 
            value="language" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="staff-language-tab"
          >
            <Globe className="w-4 h-4 mr-2" />
            {t('common.language')}
          </TabsTrigger>
        </TabsList>
        
        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6 space-y-6">
          {/* Profile Form */}
          <Card className="shadow-lg">
            <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-indigo-50">
              <CardTitle className="flex items-center gap-2 text-purple-800">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleUpdateProfile} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="staff-name">Full Name</Label>
                  <Input
                    id="staff-name"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder="Enter your name"
                    data-testid="staff-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="staff-email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="staff-email"
                      value={profile.email}
                      disabled
                      className="pl-10 bg-gray-50"
                      data-testid="staff-email-display"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Email cannot be changed</p>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="staff-save-profile-btn"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Work Information Card (Read-only) */}
          {staffInfo && (
            <Card className="shadow-lg">
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-cyan-50">
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Building2 className="w-5 h-5" />
                  Work Information
                </CardTitle>
                <CardDescription>Your employment details (contact admin to update)</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">{t("staff.department")}</p>
                    <p className="font-medium text-gray-900">{staffInfo.department || 'Not assigned'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Timezone</p>
                    <p className="font-medium text-gray-900">{staffInfo.timezone || 'UTC'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Shift Hours
                    </p>
                    <p className="font-medium text-gray-900">
                      {staffInfo.shift_start && staffInfo.shift_end 
                        ? `${staffInfo.shift_start} - ${staffInfo.shift_end}`
                        : 'Not set'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">{t("common.status")}</p>
                    <span className={`
                      px-3 py-1 rounded-full text-xs font-semibold
                      ${staffInfo.status === 'active' ? 'bg-green-100 text-green-700' : ''}
                      ${staffInfo.status === 'inactive' ? 'bg-gray-100 text-gray-700' : ''}
                    `}>
                      {staffInfo.status || 'Active'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Security Tab */}
        <TabsContent value="security" className="mt-6 space-y-6">
          <Card className="shadow-lg">
            <CardHeader className="border-b bg-gradient-to-r from-amber-50 to-orange-50">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleChangePassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwords.current_password}
                      onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                      placeholder="Enter current password"
                      required
                      data-testid="staff-current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwords.new_password}
                      onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                      placeholder="Enter new password"
                      required
                      minLength={6}
                      data-testid="staff-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Minimum 6 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwords.confirm_password}
                      onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                      placeholder="Confirm new password"
                      required
                      data-testid="staff-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="staff-change-password-btn"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Security Tips */}
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-gray-900 mb-3">Security Tips</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  Use a strong password with at least 8 characters
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  Include uppercase, lowercase, numbers, and symbols
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  Don't share your password with anyone
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  Log out when using shared computers
                </li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                {t('settings.language_settings')}
              </CardTitle>
              <CardDescription>{t('settings.select_language')}</CardDescription>
            </CardHeader>
            <CardContent>
              <LanguageSelector showLabel className="max-w-md" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Appearance
              </CardTitle>
              <CardDescription>Choose light, dark, or match your system theme</CardDescription>
            </CardHeader>
            <CardContent>
              <ThemeToggle />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default StaffSettingsPage;
